import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { hdmgdjSections } from "@/config/hdmgdjNav";
import type { HdmgdjConfig } from "@/config/hdmgdjConfig";
import { hdmgdjJumpTo, useHdmgdjHeader } from "@/hooks/hdmgdj/useHdmgdjScroll";

interface HdmgdjHeaderProps {
  config: HdmgdjConfig;
}

const HDMGDJ_LOGO =
  "/images/logo.png";

export default function HdmgdjHeader({ config }: HdmgdjHeaderProps) {
  const { hidden, scrolled } = useHdmgdjHeader();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/" || location.pathname === "";
  const solid = scrolled || menuOpen;

  const handleJump = (id: string) => (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setMenuOpen(false);
    hdmgdjJumpTo(id);
  };

  const brandNameCls = solid ? "text-foreground-950" : "text-background-50";
  const domainCls = solid ? "text-foreground-600" : "text-background-200/85";
  const navCls = solid
    ? "text-foreground-800 hover:text-primary-700"
    : "text-background-50 hover:text-primary-300";
  const burgerCls = solid
    ? "border-secondary-300 text-foreground-800"
    : "border-background-50/40 text-background-50";

  return (
    <header className={`hdmgdj-header ${solid ? "is-solid" : ""} ${hidden && !menuOpen ? "is-hidden" : ""}`}>
      <div className="hdmgdj-container">
        <div className="flex items-center justify-between h-[72px] gap-4">
          <a
            href={config.logoLink}
            className="flex items-center gap-3 shrink-0 cursor-pointer"
            aria-label={`${config.siteName} 官方主页`}
          >
            <span className="w-10 h-10 flex items-center justify-center rounded-lg overflow-hidden bg-foreground-950 shrink-0 ring-1 ring-primary-500/40">
              <img
                src={HDMGDJ_LOGO}
                alt="黄豆魔改短剧"
                title="黄豆魔改短剧官方图标"
                className="w-10 h-10 object-cover"
              />
            </span>
            <span className="flex flex-col leading-none">
              <span className={`font-heading text-[19px] tracking-wide transition-colors ${brandNameCls}`}>
                {config.siteName}
              </span>
              <span className={`mt-1 text-[11px] font-label whitespace-nowrap transition-colors ${domainCls}`}>
                {config.domain}
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7" aria-label="站内导航">
            {hdmgdjSections.map((item) =>
              onHome ? (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={handleJump(item.id)}
                  className={`text-[15px] font-label transition-colors cursor-pointer whitespace-nowrap ${navCls}`}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.id}
                  to={`/#${item.id}`}
                  className={`text-[15px] font-label transition-colors cursor-pointer whitespace-nowrap ${navCls}`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/hdmgdj-download"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-foreground-950 text-[15px] font-label font-bold whitespace-nowrap hover:bg-primary-400 transition-colors cursor-pointer"
            >
              <i className="ri-download-2-line text-lg" />
              下载APP
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开导航菜单"
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md border cursor-pointer transition-colors ${burgerCls}`}
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200">
          <div className="hdmgdj-container py-3 flex flex-col">
            {hdmgdjSections.map((item) =>
              onHome ? (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={handleJump(item.id)}
                  className="py-3 text-[15px] font-label text-foreground-800 border-b border-background-200 last:border-b-0 cursor-pointer"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.id}
                  to={`/#${item.id}`}
                  onClick={() => setMenuOpen(false)}
                  className="py-3 text-[15px] font-label text-foreground-800 border-b border-background-200 last:border-b-0 cursor-pointer"
                >
                  {item.label}
                </Link>
              )
            )}
            <Link
              to="/hdmgdj-download"
              onClick={() => setMenuOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-2 h-11 rounded-md bg-primary-500 text-foreground-950 text-[15px] font-label font-bold cursor-pointer"
            >
              <i className="ri-download-2-line text-lg" />
              下载APP
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}